import { useContext, useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import "./ProductCustomerSalesReport.scss";
import IProcessed_Report from "../../../../models/IProcessed_Report";
import moment from "moment-timezone";
import SupplierContext from "../../../../contexts/SupplierContext";
import UIService from "../../../../services/UIService";
import UserService from "@services/UserService";
import IOnGetProductCustomerSalesReportDTO from "@models/DTO/IOnGetProductCustomerSalesReportDTO";
import { SocketContext } from "../../../../contexts/SocketContext";
import UserContext from "../../../../contexts/UserContext";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IGetReportProductsDTO from "../../../../models/DTO/IGetReportProductsDTO";
import IOnGetReportProductsDTO from "../../../../models/DTO/IOnGetReportProductsDTO";
import IDownloadReportExcelDTO from "../../../../models/DTO/IDownloadReportExcelDTO";
import IOnDownloadReportExcelDTO from "../../../../models/DTO/IOnDownloadReportExcelDTO";
import PaginationBar from "../../../shared/PaginationBar/PaginationBar";
import IProcessed_Report_Detail from "../../../../models/IProcessed_Report_Detail";
import ICustomersByProduct from "@models/ICustomersByProduct";
import ReportPeriod from "../ReportPeriod/ReportPeriod";

type Props = {
    processedReport: IProcessed_Report;
};

export const ProductCustomerSalesReport = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const userModel = useContext(UserContext);

    // Check if this is a new-style report with pagination
    const isNewReport = props.processedReport.total_items !== undefined;

    const reportData = props.processedReport.report_data as IOnGetProductCustomerSalesReportDTO;

    // State for pagination and product loading
    const [products, setProducts] = useState<IProcessed_Report_Detail[]>([]);
    const [offset, setOffset] = useState(0); // PaginationBar uses 0-based offset
    const [limit, setLimit] = useState(25);
    const [totalProducts, setTotalProducts] = useState(
        isNewReport ? props.processedReport.total_items || 0 : reportData.catalogItems?.length || 0
    );
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [downloadingExcel, setDownloadingExcel] = useState(false);

    // Search and filter state
    const [searchSku, setSearchSku] = useState("");
    const [filterCategoryId, setFilterCategoryId] = useState<number | undefined>(undefined);
    const [sortBy, setSortBy] = useState<"sku" | "total_net" | "total_qty" | "sort_order">("sku");
    const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");

    const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
    const prefix = "$";

    // Fetch paginated products from the backend
    const fetchReportProducts = (currentPage: number) => {
        if (!isNewReport) return; // Skip for legacy reports

        setLoading(true);

        const payload: IGetReportProductsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            reportGuid: props.processedReport.guid,
            page: currentPage,
            limit,
            searchSku: searchSku || undefined,
            categoryId: filterCategoryId,
            sortBy,
            sortOrder,
        };

        socket.emit(EndpointsEnum.ReportGetReportProducts, payload);
    };

    // Handle product list response
    const onGetReportProducts = (data: IOnGetReportProductsDTO) => {
        setProducts(data.catalogItems);
        setTotalProducts(data.total);
        setTotalPages(data.totalPages);
        // Backend returns 1-based page, convert to 0-based offset for PaginationBar
        // setOffset((data.page - 1) * limit);
        setLoading(false);
    };

    // Download Excel file from backend
    const handleDownloadExcel = () => {
        setDownloadingExcel(true);

        const payload: IDownloadReportExcelDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            reportGuid: props.processedReport.guid,
        };

        socket.emit(EndpointsEnum.ReportDownloadExcel, payload);
    };

    // Handle Excel download response
    const onDownloadExcel = (data: IOnDownloadReportExcelDTO) => {
        setDownloadingExcel(false);

        if (data.downloadDirect && data.buffer) {
            // Small file - direct download from base64 buffer
            downloadFromBase64(data.buffer, data.filename);
        } else if (data.s3Url) {
            // Large file - download from S3
            window.open(data.s3Url, "_blank");
        }
    };

    // Convert base64 to Excel file and trigger download
    const downloadFromBase64 = (base64: string, filename: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const toggleCustomerOrders = (customerId: string) => {
        const newExpanded = new Set(expandedCustomers);
        if (newExpanded.has(customerId)) {
            newExpanded.delete(customerId);
        } else {
            newExpanded.add(customerId);
        }
        setExpandedCustomers(newExpanded);
    };

    // Socket listeners setup
    useEffect(() => {
        if (isNewReport) {
            socket.on(EndpointsEnum.ReportOnGetReportProducts, onGetReportProducts);
            socket.on(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);

            return () => {
                socket.removeListener(EndpointsEnum.ReportOnGetReportProducts, onGetReportProducts);
                socket.removeListener(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNewReport]);

    // Fetch products when component mounts or when offset, limit, search, or filters change
    useEffect(() => {
        if (isNewReport) {
            const page = offset + 1;
            // Convert 0-based offset to 1-based page for backend
            console.log("Fetching products - Offset:", offset, "Limit:", limit, "Page:", page);
            fetchReportProducts(page);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNewReport, offset, limit, searchSku, filterCategoryId, sortBy, sortOrder]);

    const getMarginClass = (margin: number): string => {
        const marginPercent = margin * 100;
        if (marginPercent >= 40) return "margin-excellent";
        if (marginPercent >= 20) return "margin-good";
        return "margin-low";
    };

    return (
        <div className={"ProductCustomerSalesReport"}>
            <div className="row">
                <OwTitle className={"col-12"} title={"product_customer_sales_report"} tag={"h3"} />
                <ReportPeriod
                    className={"col-12 mb-2"}
                    startDate={props.processedReport.period_start}
                    endDate={props.processedReport.period_end}
                />
            </div>

            {/* Report Info and Download */}
            <div className="row mb-3">
                <div className="col-12">
                    {isNewReport && (
                        <div className="d-flex align-items-center gap-3 mb-2">
                            <span>
                                <strong>Total Products:</strong> {totalProducts}
                            </span>
                            {props.processedReport.excel_file_size && (
                                <span className="text-muted">
                                    Excel File Size: {(props.processedReport.excel_file_size / 1024 / 1024).toFixed(2)} MB
                                </span>
                            )}
                        </div>
                    )}
                    <div>
                        <OwLabel
                            className={"me-3"}
                            title={downloadingExcel ? "generating_excel" : "excel"}
                            leftIconClass={downloadingExcel ? "fas fa-spinner fa-spin" : "fas fa-download"}
                            fn={handleDownloadExcel}
                        />
                    </div>
                </div>
            </div>

            <div className={"card mb-4 p-2 " + (UIService.isMainViewLight() ? "" : "bg-dark text-light")}>
                <div className="row">
                    <div className="col-3 d-flex align-items-end">
                        <OwLabel title={"grand_total"} isBold={true} />
                    </div>
                    <div className={"col-2"}></div>
                    <div className={"col-1 text-end"}>
                        <div>
                            <OwLabel title={"qty_total"} isBold={true} isUnderline={true} />
                        </div>
                        <NumericFormat
                            value={reportData.grandTotals.qty}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={supplierModel.qty_precision || 2}
                            fixedDecimalScale={true}
                            prefix={""}
                        />
                    </div>
                    <div className={"col-2 text-end"}>
                        <div>
                            <OwLabel title={"net_total"} isBold={true} isUnderline={true} />
                        </div>
                        <NumericFormat
                            value={reportData.grandTotals.net}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={prefix}
                            decimalScale={supplierModel.pricing_precision || 2}
                            fixedDecimalScale={true}
                        />
                    </div>
                    {supplierModel.has_cost_pricing ? (
                        <div className={"col-2 text-end"}>
                            <div>
                                <OwLabel title={"cost_total"} isBold={true} isUnderline={true} />
                            </div>
                            <NumericFormat
                                value={reportData.grandTotals.cost}
                                displayType={"text"}
                                thousandSeparator={true}
                                prefix={prefix}
                                decimalScale={supplierModel.pricing_precision || 2}
                                fixedDecimalScale={true}
                            />
                        </div>
                    ) : null}
                    {supplierModel.has_cost_pricing ? (
                        <div className={"col-2 text-end"}>
                            <div>
                                <OwLabel title={"margin_total"} isBold={true} isUnderline={true} />
                            </div>
                            <NumericFormat
                                value={reportData.grandTotals.margin * 100}
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={supplierModel.pricing_precision || 2}
                                fixedDecimalScale={true}
                            />
                            %
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Loading State */}
            {loading && isNewReport && (
                <div className="text-center p-4">
                    <i className="fas fa-spinner fa-spin fa-2x" />
                    <div className="mt-2">Loading products...</div>
                </div>
            )}

            {/* Products List */}
            {!loading &&
                products.map((product) => {
                    // Handle both old and new data structures
                    const productName = isNewReport ? product.catalog_item_name : (product as any).catalog_item?.name;
                    const productSku = product.sku;
                    const productGuid = product.guid || product.catalog_item_guid;
                    const totalQty = isNewReport ? product.total_qty : (product as any).totalQty;
                    const totalNet = isNewReport ? product.total_net : (product as any).totalNet;
                    const totalCost = isNewReport ? product.total_cost : (product as any).totalCost;
                    const totalMargin = isNewReport ? product.total_margin : (product as any).totalMargin;
                    const customers = isNewReport ? product.detail_data.customers : (product as any).customers;

                    return (
                        <div key={productGuid} className={"card mb-4 p-2 " + (UIService.isMainViewLight() ? "" : "bg-dark text-light")}>
                            <div className="row mb-3">
                                <div className="col-12">
                                    <h5 className="mb-1">
                                        {productName}
                                        <span className="text-muted ms-2" style={{ fontSize: "0.9rem" }}>
                                            ({productSku})
                                        </span>
                                    </h5>
                                    <div className="d-flex gap-4">
                                        <span>
                                            <strong>Total Qty:</strong>{" "}
                                            <NumericFormat
                                                value={totalQty}
                                                displayType={"text"}
                                                thousandSeparator={true}
                                                decimalScale={supplierModel.qty_precision || 2}
                                                fixedDecimalScale={true}
                                            />
                                        </span>
                                        <span>
                                            <strong>Total Revenue:</strong>{" "}
                                            <NumericFormat
                                                value={totalNet}
                                                displayType={"text"}
                                                thousandSeparator={true}
                                                prefix={prefix}
                                                decimalScale={supplierModel.pricing_precision || 2}
                                                fixedDecimalScale={true}
                                            />
                                        </span>
                                        {supplierModel.has_cost_pricing && (
                                            <>
                                                <span>
                                                    <strong>Total Cost:</strong>{" "}
                                                    <NumericFormat
                                                        value={totalCost}
                                                        displayType={"text"}
                                                        thousandSeparator={true}
                                                        prefix={prefix}
                                                        decimalScale={supplierModel.pricing_precision || 2}
                                                        fixedDecimalScale={true}
                                                    />
                                                </span>
                                                <span className={getMarginClass(totalMargin)}>
                                                    <strong>Margin:</strong>{" "}
                                                    <NumericFormat
                                                        value={totalMargin * 100}
                                                        displayType={"text"}
                                                        thousandSeparator={true}
                                                        decimalScale={supplierModel.pricing_precision || 2}
                                                        fixedDecimalScale={true}
                                                    />
                                                    %
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-3">
                                    <OwLabel title={"customer"} isBold={true} isUnderline={true} />
                                </div>
                                <div className={"col-2"}>
                                    <OwLabel title={"orders"} isBold={true} isUnderline={true} />
                                </div>
                                <div className={"col-1 text-end"}>
                                    <OwLabel title={"qty"} isBold={true} isUnderline={true} />
                                </div>
                                <div className={"col-2 text-end"}>
                                    <OwLabel title={"per_unit"} isBold={true} isUnderline={true} />
                                </div>
                                <div className={"col-2 text-end"}>
                                    <OwLabel title={"net"} isBold={true} isUnderline={true} />
                                </div>
                                {supplierModel.has_cost_pricing ? (
                                    <div className={"col-2 text-end"}>
                                        <OwLabel title={"cost"} isBold={true} isUnderline={true} />
                                    </div>
                                ) : null}
                                {supplierModel.has_cost_pricing ? (
                                    <div className={"col-2 text-end"}>
                                        <OwLabel title={"margin"} isBold={true} isUnderline={true} />
                                    </div>
                                ) : null}
                            </div>

                            {customers.map((customer: any) => {
                                const customerId = `${productGuid}-${customer.vendor.guid}`;
                                const isExpanded = expandedCustomers.has(customerId);

                                const perUnit = customer.net / customer.qty || 0;

                                return (
                                    <div key={customer.vendor.guid}>
                                        <div className="row customer-row">
                                            <div className="col-3">
                                                <div>{customer.vendor.name}</div>
                                                <div className="ms-2 text-muted" style={{ fontSize: "0.9rem" }}>
                                                    {customer.vendor.Ship_To?.city ? `${customer.vendor.Ship_To?.city}` : ""}
                                                    {customer.vendor.Ship_To?.state ? `, ${customer.vendor.Ship_To?.state}` : ""}{" "}
                                                    {customer.vendor.Ship_To?.postal_code ? ` ${customer.vendor.Ship_To?.postal_code}` : ""}
                                                </div>
                                                {(customer.vendor.Rep_Groups || []).length > 0 ? (
                                                    <div className="ms-2 text-muted" style={{ fontSize: "0.9rem" }}>
                                                        {customer.vendor.Rep_Groups.map((repGroup: any) => repGroup.name).join(", ")}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div className={"col-2"}>
                                                <button
                                                    className="btn btn-sm btn-link p-0"
                                                    onClick={() => toggleCustomerOrders(customerId)}>
                                                    <i className={isExpanded ? "fas fa-chevron-down" : "fas fa-chevron-right"} />{" "}
                                                    {customer.orders.length} order{customer.orders.length !== 1 ? "s" : ""}
                                                </button>
                                            </div>
                                            <div className={"col-1 text-end"}>
                                                <NumericFormat
                                                    value={customer.qty}
                                                    displayType={"text"}
                                                    thousandSeparator={true}
                                                    decimalScale={supplierModel.qty_precision || 2}
                                                    fixedDecimalScale={true}
                                                    prefix={""}
                                                />
                                            </div>
                                            <div className={"col-2 text-end"}>
                                                <NumericFormat
                                                    value={perUnit}
                                                    displayType={"text"}
                                                    thousandSeparator={true}
                                                    decimalScale={supplierModel.pricing_precision || 2}
                                                    fixedDecimalScale={true}
                                                    prefix={prefix}
                                                />
                                            </div>
                                            <div className={"col-2 text-end"}>
                                                <NumericFormat
                                                    value={customer.net}
                                                    displayType={"text"}
                                                    thousandSeparator={true}
                                                    decimalScale={supplierModel.pricing_precision || 2}
                                                    fixedDecimalScale={true}
                                                    prefix={prefix}
                                                />
                                            </div>
                                            {supplierModel.has_cost_pricing ? (
                                                <div className={"col-2 text-end"}>
                                                    <NumericFormat
                                                        value={customer.cost}
                                                        displayType={"text"}
                                                        thousandSeparator={true}
                                                        decimalScale={supplierModel.pricing_precision || 2}
                                                        fixedDecimalScale={true}
                                                        prefix={prefix}
                                                    />
                                                </div>
                                            ) : null}
                                            {supplierModel.has_cost_pricing ? (
                                                <div className={"col-2 text-end"}>
                                                    <NumericFormat
                                                        value={customer.margin * 100}
                                                        displayType={"text"}
                                                        thousandSeparator={true}
                                                        decimalScale={supplierModel.pricing_precision || 2}
                                                        fixedDecimalScale={true}
                                                    />
                                                    %
                                                </div>
                                            ) : null}
                                        </div>

                                        {isExpanded && (
                                            <div className="orders-list ms-4 mb-2">
                                                {customer.orders.map((order: any) => (
                                                    <div key={order.orderId} className="row order-detail">
                                                        <div className="col-3 offset-3 text-muted" style={{ fontSize: "0.9rem" }}>
                                                            {order.orderNumber}{" "}
                                                            {order.qbo_InvoiceId
                                                                ? `(${order.qbo_InvoiceId}${order.qbo_TxnDate.length > 0 ? ` - ${order.qbo_TxnDate}` : ""})`
                                                                : ""}
                                                        </div>
                                                        <div className="col-2 text-muted" style={{ fontSize: "0.9rem" }}>
                                                            {UserService.parseDate(order.date) || ""}
                                                        </div>
                                                        <div className="col-2 text-muted" style={{ fontSize: "0.9rem" }}>
                                                            <OwLabel title={"qty"} /> {order.qty}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}

            {/* Pagination Controls */}
            {isNewReport && !loading && totalProducts > 0 && (
                <div className="row mt-4">
                    <div className="col-12">
                        <PaginationBar
                            offset={offset}
                            limit={limit}
                            totalRecords={totalProducts}
                            onPaginationChanged={(newOffset, newLimit) => {
                                console.log("Pagination changed - Offset:", newOffset, "Limit:", newLimit);
                                setOffset(newOffset);
                                setLimit(newLimit);
                            }}
                            isActive={loading}
                            hideLimit={false}
                            centered={true}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
